import React from 'react';
import {CustomMapStyle, OperatorAudienceAppSettings, OperatorAudienceAppSettingsData} from "types/Operator";
import {Form, Formik, FormikHelpers} from "formik";
import FormControls from "components/FormControls";
import Selector from "components/Form/Selector";
import IconName from "types/IconName";
import Field from "components/Form/Field";
import Checkbox from "../../../components/Checkbox/Checkbox";
import {toTitleCase} from "../../../services/string";
import AppFeature from "../../../types/AppFeature";
import Locator from 'components/Form/Locator';
import FieldHeader from "../../../components/Form/FieldHeader";
import MapMode from "../../../types/MapMode";
import {Tab} from '@headlessui/react';
import TabButton from "../../../components/TabButton";
import ExhibitFeature from "../../../types/ExhibitFeature";
import {useOperator} from "../../../context/OperatorContext";
import mapStyles from "../../../components/StyleControl/MapStyles";
import {ToggleSwitch} from "../../../components/Form/Form/Control";
import * as Yup from "yup";

// Types ----

type AudienceAppFormProps = {
  settings?: OperatorAudienceAppSettings | null
  onSubmit: (values: OperatorAudienceAppSettingsData, helpers: FormikHelpers<OperatorAudienceAppSettingsData>) => any
}

// Helpers ----

const mapModes = Object.values(MapMode)

const mapStyleOptions = (customStyles: Array<CustomMapStyle>) => {
  const standardOptions = mapStyles
    .map(style => ({...style, id: style.url, icon: IconName.MAP}))

  const customOptions = customStyles
    .map(style => ({...style, id: style.url, description: 'Custom style', icon: IconName.MAP}))

  return [...standardOptions, ...customOptions]
}

// Component ----

function AudienceAppForm(props: AudienceAppFormProps) {
  const {termFor, settings} = useOperator();

  const allMapStyles = mapStyleOptions(settings?.integrations.mapbox.custom_styles ?? []);

  const validationSchema = Yup.object().shape({
    login_message: Yup.string().max(250, 'Maximum of 250 characters'),
  })

  const initialValues: OperatorAudienceAppSettingsData = {
    login_message: props.settings?.login_message || '',
    map_style: props.settings?.map_style || mapStyles[0].url,
    map_mode: props.settings?.map_mode || MapMode.AUTO,
    map_state: props.settings?.map_state || null,
    map_rotation_enabled: props.settings?.map_rotation_enabled ?? false,
    map_tilt_enabled: props.settings?.map_tilt_enabled ?? false,
    app_features: props.settings?.app_features || Object.values(AppFeature),
    exhibit_features: props.settings?.exhibit_features || Object.values(ExhibitFeature),
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => (
        <section aria-labelledby="integrations-heading">
          <Form onKeyDown={event => {
            if (event.ctrlKey && event.key === 'Enter') formik.handleSubmit()
          }}>
            <div className="shadow sm:rounded-md">
              <div className="bg-white sm:rounded-t-md py-6 px-4 sm:p-6">
                <div>
                  <h2 id="integrations-heading" className="text-lg leading-6 font-medium text-gray-900">
                    Audience App
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure your audience app
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-6 gap-x-6 gap-y-8">
                  {/* Signup Message */}
                  <div className="col-span-6 md:col-span-6">
                    {/* TODO: Rename field to "signup_message" or similar */}
                    <Field
                      type="text"
                      label="Signup Message"
                      hint="Optional. 250 chars max."
                      name="login_message"
                      id="login_message"
                      placeholder="Enter information to be shown on the sign-up screen"
                      maxLength={250}
                    />
                  </div>

                  <fieldset className="col-span-6">
                    <div>
                      <legend className="text-base font-medium text-gray-900">Interactive Map</legend>
                    </div>

                    <div className="mt-2 grid grid-cols-6 gap-6">
                      {/* Map Style */}
                      <div className="col-span-6 md:col-span-3">
                        <Selector
                          label="Style"
                          items={allMapStyles}
                          selectedId={initialValues.map_style}
                          onChange={item => formik.setFieldValue('map_style', item.id)}
                          required
                        />
                      </div>

                      {/* Map Start */}
                      <div className="col-span-6 md:col-span-6">
                        <FieldHeader label="Starting Position"/>
                        <Tab.Group
                          selectedIndex={mapModes.indexOf(formik.values.map_mode || MapMode.AUTO)}
                          onChange={index => {
                            void formik.setFieldValue('map_state', null)
                            void formik.setFieldValue('map_mode', mapModes[index])
                          }}
                        >
                          <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                            <TabButton position={TabButton.positions.FIRST}>Auto</TabButton>
                            <TabButton position={TabButton.positions.LAST}>Custom</TabButton>
                          </Tab.List>

                          <Tab.Panels>
                            <Tab.Panel></Tab.Panel>
                            <Tab.Panel>
                              <div className="mt-1">
                                <Locator
                                  height={'32rem'}
                                  type="view"
                                  onViewChange={state => formik.setFieldValue('map_state', state)}
                                />
                              </div>
                            </Tab.Panel>
                          </Tab.Panels>
                        </Tab.Group>
                      </div>

                      {/* Map Rotation */}
                      <div className="col-span-6 md:col-span-6">
                        <ToggleSwitch
                          label="Rotation"
                          description="When enabled, allows audience members to rotate the map"
                          checked={formik.values.map_rotation_enabled}
                          onChange={enabled => formik.setFieldValue('map_rotation_enabled', enabled)}
                          multiline
                        />
                      </div>

                      {/* Map Tilt */}
                      <div className="col-span-6 md:col-span-6">
                        <ToggleSwitch
                          label="Tilt"
                          description="When enabled, allows audience members to tilt the map"
                          checked={formik.values.map_tilt_enabled}
                          onChange={enabled => formik.setFieldValue('map_tilt_enabled', enabled)}
                          multiline
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* Enabled App Features */}
                  <div className="col-span-6 md:col-span-6">
                    <fieldset className="space-y-4">
                      <div>
                        <legend className="text-base font-medium text-gray-900">Enabled App Features</legend>
                        <p className="text-sm text-gray-500">
                          Select which audience app features are enabled
                        </p>
                      </div>

                      <div className="space-y-4 md:space-y-0 md:flex md:space-x-6">
                        {Object.values(AppFeature).map((feature, index) => (
                          <Checkbox
                            key={feature}
                            id={`app_features.${index}`}
                            name="app_features"
                            value={feature}
                            label={toTitleCase(feature)}
                            readonly={feature === AppFeature.OVERVIEW}
                          />
                        ))}
                      </div>

                    </fieldset>
                  </div>

                  {/* Enabled Exhibit Features */}
                  <div className="col-span-6 md:col-span-6">
                    <fieldset className="space-y-4">
                      <div>
                        <legend className="text-base font-medium text-gray-900">Enabled Exhibit Features</legend>
                        <p className="text-sm text-gray-500">
                          Select which {termFor('exhibit')} features are enabled
                        </p>
                      </div>

                      <div className="space-y-4 md:space-y-0 md:flex md:space-x-6">
                        {Object.values(ExhibitFeature).map((feature, index) => (
                          <Checkbox
                            key={feature}
                            id={`exhibit_features.${index}`}
                            name="exhibit_features"
                            value={feature}
                            label={toTitleCase(feature)}
                            readonly={feature === ExhibitFeature.INFORMATION}
                          />
                        ))}
                      </div>

                    </fieldset>
                  </div>
                </div>
              </div>

              <FormControls formik={formik} compact withoutCancel/>
            </div>
          </Form>
        </section>
      )}
    </Formik>
  );
}

export default AudienceAppForm;